/**
 * Benchmark addon — provides the Benchmark page in the admin UI AND
 * exposes the benchmark surface (runPipelineTest / decoder-perf /
 * history CRUD) via customActions, dispatched through
 * `api.addons.custom.mutate({ addonId: 'benchmark', action, input })`.
 *
 * All pipeline inference routes through
 * `ctx.api.pipelineExecutor.runPipeline` — the addon is a pure
 * orchestrator + persistence layer. The decoder performance test is
 * likewise a pure cap-level orchestrator: it drives the registered
 * `decoder` cap via tRPC primitives and polls `metrics-provider` for
 * process-level stats. No direct class imports, no ad-hoc worker
 * forks — any decoder provider that registers the cap is testable
 * without modification.
 */

import * as fs from 'node:fs'
import { fileURLToPath } from 'node:url'
import * as path from 'node:path'
import type {
  AddonApi,
  AddonInitResult,
  AddonPageDeclaration,
  DurableState,
  FrameResult,
  IAddonPageProvider,
  PipelineDefaultStep,
  PipelineStepInput,
} from '@camstack/types'
import { BaseAddon, addonPagesSourceCapability, parseJsonUnknown, errMsg } from '@camstack/types'
import { z } from 'zod'
import { benchmarkActions, type BenchmarkActions } from './benchmark-actions.js'
import {
  DecoderFixtureSchema,
  DecoderPerfHistoryEntrySchema,
  HistoryEntrySchema,
  PipelineBenchmarkHistoryEntrySchema,
} from './benchmark-schemas.js'
import { createHash } from 'node:crypto'
import { runDecoderPerfTest } from './decoder-perf-runner.js'
import { ReplayDecoder } from './replay/replay-decode.js'
import { ReplayStore, type StoredReplayRun } from './replay/replay-store.js'
import {
  orchestrateReplayRun,
  type FetchSourceBytesResult,
  type OrchestrateReplayRunResult,
} from './replay/replay-wiring.js'
import { diffReplayRuns, type ReplayDiff } from './replay/replay-diff.js'
import type { ReplaySource } from './replay/replay-source.js'
import { runSustainedLoop, type BatchDispatchResult } from './throughput/sustained-runner.js'
import {
  computeAvgDetections,
  computeDetsPerSec,
  computeFps,
  computeLatencyStats,
} from './throughput/throughput-stats.js'

// ── Persistence ────────────────────────────────────────────────────────────
const KEY_BENCHMARK_HISTORY = 'benchmarkHistory'
const MAX_HISTORY_ENTRIES = 100
/** Empty history in the persisted encoding (a JSON string — see historyState). */
const EMPTY_HISTORY_JSON = '[]'

/**
 * Hard backstop for a single benchmark inference call. The server-side pool
 * request is bounded (POOL_INFER_TIMEOUT_MS ~60s), so a normal run completes
 * well under this; this addon-level guard ensures the UI can NEVER hang on
 * "loading" forever even if the stall is upstream of the pool (e.g. a stalled
 * first-run model download, a routing wedge). Generous — a single full-tree
 * run is far below it — and env-overridable for slow hardware / cold models.
 */
const BENCH_RUN_TIMEOUT_MS = Math.max(
  10_000,
  Number(process.env['CAMSTACK_BENCH_RUN_TIMEOUT_MS']) || 120_000,
)

/**
 * Race `promise` against a hard timeout. On timeout the returned promise
 * REJECTS with a clear, operator-facing message (never resolves silently) so
 * the caller surfaces an error instead of hanging. The pending work is left to
 * settle on its own (it can't be cancelled through the cap boundary).
 */
export async function withHardTimeout<T>(
  promise: Promise<T>,
  ms: number,
  label: string,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(
      () => reject(new Error(`${label} exceeded ${ms}ms — aborting (likely a wedged step)`)),
      ms,
    )
    timer.unref?.()
  })
  try {
    return await Promise.race([promise, timeout])
  } finally {
    if (timer) clearTimeout(timer)
  }
}

type HistoryEntry = z.infer<typeof HistoryEntrySchema>
type PipelineHistoryEntry = z.infer<typeof PipelineBenchmarkHistoryEntrySchema>
type DecoderPerfHistoryEntry = z.infer<typeof DecoderPerfHistoryEntrySchema>
type DecoderFixture = z.infer<typeof DecoderFixtureSchema>

const HistoryArraySchema = z.array(HistoryEntrySchema)

// ── Decoder-perf fixtures (shipped inside the addon under assets/) ─────────

/** Metadata describing each shipped fixture — keyed by on-disk filename. */
interface FixtureMeta {
  readonly codec: 'h264' | 'h265'
  readonly resolutionLabel: string
  readonly description: string
}
const FIXTURE_META: Readonly<Record<string, FixtureMeta>> = {
  'camera-sim-360p.h264': {
    codec: 'h264',
    resolutionLabel: '360p',
    description: 'Synthetic 360p H.264 — light workload',
  },
  'camera-sim-1080p.h264': {
    codec: 'h264',
    resolutionLabel: '1080p',
    description: 'Synthetic 1080p H.264 — production-like',
  },
  'camera-sim-1440p-h265.hevc': {
    codec: 'h265',
    resolutionLabel: '1440p',
    description: 'Synthetic 1440p H.265 — hardware decode stress',
  },
}

/**
 * Resolve the addon's bundled fixtures directory.
 *
 * The addon ships as an ESM module and the compiled bundle lives under
 * `<addon-root>/dist/` while `assets/fixtures/` sits beside it in the
 * package root. `import.meta.url` always points at the bundle, so walking
 * up one directory lands on the package root regardless of whether the
 * addon runs from source (tsx) or from the built bundle.
 */
function resolveFixturesDir(): string {
  const here = fileURLToPath(import.meta.url)
  const pkgRoot = path.resolve(path.dirname(here), '..')
  return path.join(pkgRoot, 'assets', 'fixtures')
}

/**
 * Benchmark addon — provides the Benchmark page in the admin UI.
 */
export class BenchmarkAddon extends BaseAddon {
  readonly id = 'benchmark'

  /** Per-sessionId abort flags for in-flight decoder perf runs. */
  private readonly decoderPerfAbortFlags = new Map<string, { aborted: boolean }>()

  /** Per-sessionId abort flags for in-flight sustained-throughput runs. */
  private readonly sustainedAbortFlags = new Map<string, { aborted: boolean }>()

  /**
   * In-memory cache of original JPEG bytes keyed by `frameId`. Populated by
   * `cacheBenchFrame` (UI uploads JPEG once at bench start). Subsequent
   * `runPipelineTest` calls ship only `frameId` — bench addon forwards the
   * cached JPEG buffer as `image: Uint8Array` to pipelineExecutor.runPipeline
   * (Moleculer MsgPack ships binary efficiently). Why JPEG and not raw RGB:
   * cross-process MsgPack of a 2.7MB raw RGB frame (1280×720×3) is 13×
   * heavier than the 200KB JPEG, and the pipeline addon's per-call sharp
   * decode (~5ms) is cheaper than serialising the raw buffer twice.
   * Production's broker→detection path is in-process (same group) so neither
   * cost matters there.
   */
  private readonly frameCache = new Map<
    string,
    {
      readonly jpeg: Buffer
      readonly width: number
      readonly height: number
      readonly expiresAt: number
    }
  >()

  /** Periodic GC handle — clears expired frames every 60s. */
  private frameCacheSweeper: NodeJS.Timeout | null = null

  /** The replay run collection — declared in `onInitialize`, before any
   *  `runReplayClip` call can save into it. See `replay/replay-store.ts`. */
  private replayStore: ReplayStore | null = null
  /** The replay's one ffmpeg spawn, bounded — see `replay/replay-decode.ts`. */
  private readonly replayDecoder = new ReplayDecoder({
    ffmpegPath: process.env['FFMPEG_PATH'] ?? 'ffmpeg',
  })
  /** `replay:<StoredReplayRun>` — an in-memory index would drift from the
   *  store; this is a thin cache over `replayStore.list`, keyed for the
   *  diff action's two-id lookup. */
  private replayRunCache = new Map<string, StoredReplayRun>()

  readonly pages: readonly AddonPageDeclaration[] = [
    {
      id: 'benchmark',
      label: 'Benchmark',
      icon: 'gauge',
      path: '/addon/benchmark',
      remoteName: 'addon_benchmark_page',
      bundle: 'remoteEntry.js',
      // Dock into the sidebar's Detection group.
      section: 'detection',
    },
  ] as const

  constructor() {
    super({})
  }

  protected async onInitialize(): Promise<AddonInitResult<BenchmarkActions>> {
    const provider: IAddonPageProvider = {
      id: this.id,
      listPages: () => this.pages,
    }

    // MUST be declared before the first `save` — an undeclared collection
    // crash-loops the runner (same rule every declared collection in this
    // repo follows).
    await ReplayStore.declare(this.api.settingsStore)
    this.replayStore = new ReplayStore(this.api.settingsStore)

    return {
      providers: [{ capability: addonPagesSourceCapability, provider }],
      customActions: benchmarkActions,
      actionHandlers: {
        runPipelineTest: async (input) => this.runPipelineTest(input),
        runPipelineBatchTest: async (input) => this.runPipelineBatchTest(input),
        runMultiCameraStressTest: async (input) => this.runMultiCameraStressTest(input),
        runSustainedThroughput: async (input) => this.runSustainedThroughput(input),
        abortSustainedThroughput: async (input) => this.abortSustainedThroughput(input),
        cacheBenchFrame: async (input) => this.cacheBenchFrame(input),
        releaseBenchFrame: async (input) => this.releaseBenchFrame(input),
        listBenchmarkHistory: async () => this.listBenchmarkHistory(),
        saveBenchmarkResult: async (input) => this.saveBenchmarkResult(input),
        saveDecoderPerformanceResult: async (input) => this.saveDecoderPerformanceResult(input),
        deleteBenchmarkResult: async (input) => this.deleteBenchmarkResult(input),
        clearBenchmarkHistory: async () => this.clearBenchmarkHistory(),
        listDecoderFixtures: async () => this.listDecoderFixtures(),
        runDecoderPerformanceTest: async (input) => this.runDecoderPerformanceTest(input),
        abortDecoderPerformanceTest: async (input) => this.abortDecoderPerformanceTest(input),
        uploadReplayClip: async (input) => this.uploadReplayClip(input),
        runReplayClip: async (input) => this.runReplayClip(input),
        listReplayRuns: async (input) => this.listReplayRuns(input),
        diffReplayRuns: async (input) => this.diffReplayRunsAction(input),
      },
    }
  }

  protected async onShutdown(): Promise<void> {
    if (this.frameCacheSweeper) {
      clearInterval(this.frameCacheSweeper)
      this.frameCacheSweeper = null
    }
    this.frameCache.clear()
    // Signal any in-flight sustained runs to drain on the next batch boundary.
    for (const flag of this.sustainedAbortFlags.values()) flag.aborted = true
    this.sustainedAbortFlags.clear()
  }

  // ── Frame cache (raw RGB) ────────────────────────────────────────────────

  private async cacheBenchFrame(
    input: z.infer<BenchmarkActions['cacheBenchFrame']['input']>,
  ): Promise<z.infer<BenchmarkActions['cacheBenchFrame']['output']>> {
    const jpeg = Buffer.from(input.imageBase64, 'base64')
    // Probe dimensions only — keep the JPEG bytes (200KB) rather than
    // decoding to raw RGB (2.7MB). The pipeline addon decodes once per
    // call via sharp (~5ms). Storing JPEG keeps the bench→pipeline
    // Moleculer payload light, matching the original (pre-cache) hop.
    const sharp = (await import('sharp')).default
    const meta = await sharp(jpeg).metadata()
    const width = meta.width ?? 0
    const height = meta.height ?? 0
    if (width <= 0 || height <= 0) {
      throw new Error('cacheBenchFrame: failed to probe JPEG dimensions')
    }
    const ttlMs = Math.max(60_000, (input.ttlSeconds ?? 600) * 1000)
    const frameId = `bench-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
    const expiresAt = Date.now() + ttlMs
    this.frameCache.set(frameId, { jpeg, width, height, expiresAt })
    if (!this.frameCacheSweeper) {
      this.frameCacheSweeper = setInterval(() => this.sweepFrameCache(), 60_000)
      this.frameCacheSweeper.unref?.()
    }
    this.ctx.logger.info('cached bench frame', {
      meta: { frameId, width, height, jpegBytes: jpeg.length, ttlMs },
    })
    return { frameId, width, height, expiresAt }
  }

  private async releaseBenchFrame(
    input: z.infer<BenchmarkActions['releaseBenchFrame']['input']>,
  ): Promise<z.infer<BenchmarkActions['releaseBenchFrame']['output']>> {
    return { released: this.frameCache.delete(input.frameId) }
  }

  private sweepFrameCache(): void {
    const now = Date.now()
    for (const [id, entry] of this.frameCache) {
      if (entry.expiresAt < now) this.frameCache.delete(id)
    }
  }

  // ── Handler implementations ──────────────────────────────────────────────

  private get api(): AddonApi {
    const api = this.ctx.api
    if (!api) throw new Error('BenchmarkAddon: ctx.api not available')
    return api
  }

  private async runPipelineTest(
    input: z.infer<BenchmarkActions['runPipelineTest']['input']>,
  ): Promise<FrameResult> {
    const { nodeId, steps, imageBase64, referenceImage, frameId, sessionId, engine } = input
    const { deviceKey, plane } = input

    // Diag: log the exact payload the UI sent so an operator can
    // correlate slow / weird benchmark runs with the override matrix
    // (engine triple, ref image vs uploaded base64, step tree size).
    // Image bytes themselves are skipped — we just record their size.
    // Debug-level so 200-iter bench loops don't spam the journal.
    this.ctx.logger.debug('runPipelineTest payload', {
      meta: {
        nodeId: nodeId ?? null,
        deviceKey: deviceKey ?? null,
        plane: plane ?? null,
        sessionId: sessionId ?? null,
        engine: engine ?? null,
        stepsCount: steps.length,
        steps: steps.map((s) => `${s.addonId}/${s.modelId}${s.enabled ? '' : '[off]'}`),
        imageSource: frameId
          ? `frame:${frameId}`
          : referenceImage
            ? `ref:${referenceImage}`
            : imageBase64
              ? `base64:${Math.round(imageBase64.length / 1024)}KB`
              : 'none',
      },
    })

    // Cached-JPEG path: UI uploaded the image once and got a frameId; we
    // ship the cached JPEG bytes via Moleculer MsgPack on every call. Saves
    // the 265KB→200KB superjson/WS leg from browser to hub on each iter,
    // which was dominating the bench's transport metric.
    const cached = frameId ? this.frameCache.get(frameId) : undefined
    if (frameId && !cached)
      throw new Error(`runPipelineTest: frameId ${frameId} not in cache (expired or never cached)`)
    // Copy into a fresh Uint8Array — view types over a shared ArrayBufferLike
    // collide with the runPipeline schema's `Uint8Array<ArrayBuffer>` strict
    // typing and a per-call ~200KB copy is negligible vs the Moleculer hop.
    const cachedImage = cached ? new Uint8Array(cached.jpeg) : undefined

    // Fallback path — UI hasn't cached yet (single-shot click, ref image).
    // Browser → benchmark worker carries `imageBase64` over superjson/WS
    // (superjson can't ship binary efficiently). Benchmark worker decodes
    // ONCE and forwards raw bytes to the pipeline-executor cap via the
    // `image` field — Moleculer MsgPack ships Uint8Array natively, cutting
    // the 33% base64 overhead on every iteration of a burst benchmark.
    const image =
      cachedImage ?? (imageBase64 ? new Uint8Array(Buffer.from(imageBase64, 'base64')) : undefined)

    const stepsToRun =
      steps.length > 0
        ? [...steps]
        : await (async (): Promise<PipelineStepInput[]> => {
            const globalSteps = await this.api.pipelineExecutor.getGlobalSteps.query({ nodeId })
            if (!globalSteps || globalSteps.length === 0) {
              throw new Error('No pipeline steps configured')
            }
            const toInput = (step: PipelineDefaultStep): PipelineStepInput => ({
              addonId: step.addonId,
              modelId: step.modelId,
              enabled: step.enabled,
              children: step.children.map(toInput),
            })
            return globalSteps.map(toInput)
          })()

    return withHardTimeout(
      this.api.pipelineExecutor.runPipeline.mutate({
        nodeId,
        steps: stepsToRun,
        ...(image ? { image } : {}),
        ...(!image && imageBase64 ? { imageBase64 } : {}),
        referenceImage,
        sessionId,
        ...(engine ? { engine } : {}),
        // Absent, never present-and-undefined: the generated router takes
        // `.loose()` input and an explicit `undefined` is not the same thing
        // as "the node's default pool" / "the cap's default plane".
        ...(deviceKey ? { deviceKey } : {}),
        ...(plane ? { plane } : {}),
      }),
      BENCH_RUN_TIMEOUT_MS,
      'runPipelineTest',
    )
  }

  // ── Replay ────────────────────────────────────────────────────────────

  /**
   * Storage location a replay upload lands on. `replayClips` is a NEW
   * `StorageLocationType` (an open string, no schema change needed — see
   * `storage-location.ts:19`) but it still needs an actual location
   * REGISTERED for that type before `storage.write`/`.read` can resolve it
   * (`storage.upsertLocation`, `cardinality: 'single'`, its own retention).
   * That registration is an operator/boot-time provisioning step this
   * change does not perform — see the report's open items.
   */
  private static readonly REPLAY_CLIPS_LOCATION = 'replayClips'

  private async uploadReplayClip(
    input: z.infer<BenchmarkActions['uploadReplayClip']['input']>,
  ): Promise<z.infer<BenchmarkActions['uploadReplayClip']['output']>> {
    const bytes = Buffer.from(input.dataBase64, 'base64')
    // Content-hash id: two uploads of the SAME bytes land on the SAME
    // relativePath, so a re-upload overwrites rather than duplicating —
    // the plan's Task A4 requirement that two runs can prove they saw the
    // same input, and that a repeat upload never doubles disk usage.
    const clipId = `clip:${createHash('sha256').update(bytes).digest('hex').slice(0, 32)}`
    await this.api.storage.write.mutate({
      location: BenchmarkAddon.REPLAY_CLIPS_LOCATION,
      relativePath: `${clipId}.bin`,
      data: new Uint8Array(bytes),
    })
    this.ctx.logger.info('replay: clip uploaded', { meta: { clipId, bytes: bytes.length } })
    return { clipId }
  }

  private async runReplayClip(
    input: z.infer<BenchmarkActions['runReplayClip']['input']>,
  ): Promise<OrchestrateReplayRunResult> {
    if (this.replayStore === null) throw new Error('replay: store not initialized')
    const store = this.replayStore
    const source = input.source
    const result = await orchestrateReplayRun(
      {
        api: this.api,
        logger: this.ctx.logger,
        decoder: this.replayDecoder,
        store,
        ffmpegPath: process.env['FFMPEG_PATH'] ?? 'ffmpeg',
        resolveBaseTree: (deviceId) =>
          this.api.pipelineOrchestrator.resolvePipeline.query({ deviceId }),
        fetchSourceBytes: (src) => this.fetchReplaySourceBytes(src),
      },
      {
        deviceId: input.deviceId,
        nodeId: input.nodeId,
        source,
        overlay: input.overlay,
        fps: input.fps,
        maxFrames: input.maxFrames,
        maxWidth: input.maxWidth,
        ...(input.deviceKey !== undefined ? { deviceKey: input.deviceKey } : {}),
      },
    )
    if (result.kind === 'ran')
      this.replayRunCache.set(result.replayRunId, {
        id: result.replayRunId,
        deviceId: input.deviceId,
        nodeId: input.nodeId,
        clipId:
          source.kind === 'upload'
            ? source.clipId
            : `recording:${source.deviceId}:${source.fromMs}`,
        createdAtMs: Date.now(),
        source,
        plan: null,
        verdict: result.verdict,
      })
    return result
  }

  /**
   * The profile a replay window is read from. `high` — same choice
   * `RECORDED_STILL_PROFILE` makes in `addon-post-analysis` — because the
   * whole point of `recording` over `upload` is NATIVE pixels (`-c:v copy`
   * means the recorded frame IS the live frame); a window cut from a
   * lower-rate rendition would be a worse version of the thing this source
   * exists to avoid.
   */
  private static readonly REPLAY_RECORDING_PROFILE = 'high'

  /**
   * Byte source for the replay decode step.
   *
   * `upload` is `storage.read` against the `replayClips` location. `recording`
   * is `recording.getDeviceConfig` → `recording.locateSegment` →
   * `recording.readWindowBytes` — the same "cheap question first" ordering
   * `RecordedStillService` uses (a camera that is not recording costs a
   * settings read, never a disk read), through `ctx.api` because addons never
   * import each other.
   *
   * Every expected failure — not recording, no footage covering `fromMs`, a
   * window that runs past its covering segment — returns a NAMED `refused`,
   * never a thrown error: "the window has no segments" is a RESULT this
   * whole feature exists to make legible (see the plan's stated unknown about
   * whether device 615's footage covers 2026-08-26 18:12), not a failure to
   * hide behind a generic exception.
   */
  private async fetchReplaySourceBytes(source: ReplaySource): Promise<FetchSourceBytesResult> {
    if (source.kind === 'upload') {
      try {
        const bytes = await this.api.storage.read.query({
          location: BenchmarkAddon.REPLAY_CLIPS_LOCATION,
          relativePath: `${source.clipId}.bin`,
        })
        return { kind: 'ok', bytes, startMs: 0 }
      } catch (err) {
        return { kind: 'refused', reason: `clip-not-found: ${errMsg(err)}` }
      }
    }

    const profile = BenchmarkAddon.REPLAY_RECORDING_PROFILE
    const tags = { deviceId: source.deviceId }
    const config = await this.api.recording.getDeviceConfig.query({ deviceId: source.deviceId })
    if (config.enabled === false) {
      this.ctx.logger.warn('replay: recording source refused — camera is not recording', {
        tags,
      })
      return {
        kind: 'refused',
        reason: `no-recording: device ${source.deviceId} does not have recording enabled`,
      }
    }

    const located = await this.api.recording.locateSegment.query({
      deviceId: source.deviceId,
      profile,
      epochMs: source.fromMs,
    })
    if (located.kind !== 'segment') {
      this.ctx.logger.warn(
        'replay: recording source refused — no footage covers the window start',
        { tags, meta: { fromMs: source.fromMs, toMs: source.toMs, profile } },
      )
      return {
        kind: 'refused',
        reason:
          `no-footage: device ${source.deviceId} has no ${profile} recording covering ` +
          `${new Date(source.fromMs).toISOString()}`,
      }
    }

    const window = await this.api.recording.readWindowBytes.query({
      deviceId: source.deviceId,
      profile,
      startMs: located.startMs,
      fromMs: source.fromMs,
      toMs: source.toMs,
    })
    if (window.kind !== 'ok') {
      this.ctx.logger.warn(
        'replay: recording source refused — window runs past the covering segment',
        {
          tags,
          meta: { fromMs: source.fromMs, toMs: source.toMs, segmentEndMs: window.segmentEndMs },
        },
      )
      return {
        kind: 'refused',
        reason:
          `window-spans-multiple-segments: footage for device ${source.deviceId} changes segment ` +
          `at ${window.segmentEndMs}, before the requested end ${source.toMs} — shorten the window ` +
          'or align it to one segment',
      }
    }
    if (!window.reachesRequestedEnd) {
      this.ctx.logger.warn(
        'replay: recording window bytes truncated by the recorder read safety cap',
        { tags, meta: { fromMs: source.fromMs, toMs: source.toMs, gopDurMs: window.gopDurMs } },
      )
    }
    return { kind: 'ok', bytes: window.data, startMs: window.gopStartMs }
  }

  private async listReplayRuns(
    input: z.infer<BenchmarkActions['listReplayRuns']['input']>,
  ): Promise<readonly StoredReplayRun[]> {
    if (this.replayStore === null) return []
    const rows = await this.replayStore.list({ deviceId: input.deviceId })
    for (const row of rows) this.replayRunCache.set(row.id, row)
    return rows
  }

  /** `verdict` round-trips through the store as opaque JSON
   *  (`StoredReplayRun.verdict: unknown`) — this is the ONE narrowing back
   *  to the shape `diffReplayRuns` needs, at the ONE place that reads it
   *  for that purpose. Same documented boundary `ReplayStore.list` itself
   *  crosses (`replay-store.ts`): this addon is the only reader that knows
   *  what it wrote. */
  private static verdictTracksAndDiscarded(
    verdict: unknown,
  ): Pick<Parameters<typeof diffReplayRuns>[0], 'tracks' | 'discarded'> {
    return verdict as unknown as Pick<Parameters<typeof diffReplayRuns>[0], 'tracks' | 'discarded'> // TECH-DEBT: settings-store boundary, see `replay-store.ts`.
  }

  private async diffReplayRunsAction(
    input: z.infer<BenchmarkActions['diffReplayRuns']['input']>,
  ): Promise<ReplayDiff> {
    const a = this.replayRunCache.get(input.runIdA)
    const b = this.replayRunCache.get(input.runIdB)
    if (a === undefined || b === undefined) {
      throw new Error(
        'replay: diff — one or both run ids are not loaded (call listReplayRuns first)',
      )
    }
    return diffReplayRuns(
      {
        clipId: a.clipId,
        nodeId: a.nodeId,
        settings: {},
        ...BenchmarkAddon.verdictTracksAndDiscarded(a.verdict),
      },
      {
        clipId: b.clipId,
        nodeId: b.nodeId,
        settings: {},
        ...BenchmarkAddon.verdictTracksAndDiscarded(b.verdict),
      },
    )
  }

  /**
   * Batched pipeline tester.
   *
   * Decodes the JPEG once via sharp into RGB24 at native dimensions,
   * then ships N copies as `FrameInput[]` in a single
   * `pipelineExecutor.runPipelineBatch` cap call. The Python pool
   * collapses the N predicts into one `MSG_INFER_BATCH = 0x03` IPC
   * frame — per-call header + JSON envelope cost is amortised N:1.
   *
   * Returns N `FrameResult`s plus aggregate timing so the UI can
   * compute fps directly from the wall clock.
   */
  private async runPipelineBatchTest(
    input: z.infer<BenchmarkActions['runPipelineBatchTest']['input']>,
  ): Promise<z.infer<BenchmarkActions['runPipelineBatchTest']['output']>> {
    // Diag: same shape as runPipelineTest log — UI sends batchSize +
    // optional targetSize on top, capture both so the operator can
    // distinguish per-call vs batched runs at a glance.
    // Debug-level so 200-iter bench loops don't spam the journal.
    this.ctx.logger.debug('runPipelineBatchTest payload', {
      meta: {
        nodeId: input.nodeId ?? null,
        sessionId: input.sessionId ?? null,
        engine: input.engine ?? null,
        batchSize: input.batchSize,
        targetSize: input.targetSize ?? null,
        stepsCount: input.steps.length,
        steps: input.steps.map((s) => `${s.addonId}/${s.modelId}${s.enabled ? '' : '[off]'}`),
        imageBytesKB: Math.round(input.imageBase64.length / 1024),
      },
    })
    const { nodeId, steps, imageBase64, batchSize, sessionId, engine } = input
    const targetSize = input.targetSize ?? 640
    if (steps.length === 0) {
      throw new Error('runPipelineBatchTest: empty steps')
    }
    const sharp = (await import('sharp')).default
    const jpeg = Buffer.from(imageBase64, 'base64')
    // Square-resize to model input dimensions BEFORE shipping the
    // batch to detection-pipeline. Without this the cluster TCP
    // transporter's 10 MB `maxPacketSize` would reject batches of full
    // 1080p frames (4×6.2 MB = 24.8 MB) and tear the worker down.
    const rgb = await sharp(jpeg)
      .resize({ width: targetSize, height: targetSize, fit: 'fill' })
      .raw()
      .toColorspace('srgb')
      .toBuffer()
    const rgbBytes = new Uint8Array(rgb.buffer, rgb.byteOffset, rgb.byteLength)
    this.ctx.logger.debug('runPipelineBatchTest dispatch', {
      meta: {
        phase: 'batch',
        batchSize,
        targetSize,
        rgbBytes: rgb.byteLength,
        totalBytes: rgb.byteLength * batchSize,
      },
    })
    const frames = Array.from({ length: batchSize }, () => ({
      data: rgbBytes,
      format: 'rgb' as const,
      width: targetSize,
      height: targetSize,
      timestamp: Date.now(),
    }))
    const callStart = Date.now()
    const out = await withHardTimeout(
      this.api.pipelineExecutor.runPipelineBatch.mutate({
        nodeId,
        steps: [...steps],
        frames,
        sessionId,
        ...(engine ? { engine } : {}),
      }),
      BENCH_RUN_TIMEOUT_MS,
      'runPipelineBatchTest',
    )
    const callMs = Date.now() - callStart
    const inferMs = out.results.reduce((acc, r) => Math.max(acc, r.debug?.totalInferenceMs ?? 0), 0)
    return {
      results: out.results,
      callMs,
      inferMs,
    }
  }

  /**
   * Multi-camera stress tester.
   *
   * Runs the entire stress workload IN-PROCESS:
   *   1. Decode + sharp-resize the JPEG once.
   *   2. Build pre-fabricated `FrameInput[]` templates for the batched
   *      and single-frame call shapes.
   *   3. Spawn `simulatedCameras` parallel async loops; each loop runs
   *      `batchesPerCamera` rounds. Per round: fan out 1 batched call
   *      + `extraSinglesPerRound` single calls via `Promise.all`.
   *   4. Aggregate detsPerSec from `(simulatedCameras × batchesPerCamera
   *      × batchSize) / totalMs` so the metric is comparable to the
   *      external detector-engine benchmark we're calibrating against.
   *
   * Because the dispatch goes through `this.api.pipelineExecutor.
   * runPipelineBatch.mutate(...)` and not over the wire, `localProvider
   * Link` short-circuits when detection-pipeline is co-located on the
   * same node. Removes per-call JSON marshalling, JPEG decode, and TLS.
   */
  private async runMultiCameraStressTest(
    input: z.infer<BenchmarkActions['runMultiCameraStressTest']['input']>,
  ): Promise<z.infer<BenchmarkActions['runMultiCameraStressTest']['output']>> {
    const targetSize = input.targetSize ?? 640
    const simulatedCameras = input.simulatedCameras ?? 4
    const batchesPerCamera = input.batchesPerCamera ?? 125
    const batchSize = input.batchSize ?? 4
    const extraSinglesPerRound = input.extraSinglesPerRound ?? 3
    const { nodeId, steps, sessionId } = input

    if (steps.length === 0) {
      throw new Error('runMultiCameraStressTest: empty steps')
    }

    this.ctx.logger.info('runMultiCameraStressTest start', {
      meta: {
        nodeId: nodeId ?? null,
        simulatedCameras,
        batchesPerCamera,
        batchSize,
        extraSinglesPerRound,
        targetSize,
        steps: steps.map((s) => `${s.addonId}/${s.modelId}${s.enabled ? '' : '[off]'}`),
      },
    })

    // Decode + resize ONCE on the addon. Subsequent rounds reuse the
    // raw RGB buffer — no sharp work in the hot loop.
    const sharp = (await import('sharp')).default
    const jpeg = Buffer.from(input.imageBase64, 'base64')
    const rgb = await sharp(jpeg)
      .resize({ width: targetSize, height: targetSize, fit: 'fill' })
      .raw()
      .toColorspace('srgb')
      .toBuffer()
    const rgbBytes = new Uint8Array(rgb.buffer, rgb.byteOffset, rgb.byteLength)

    interface FramePayload {
      readonly data: Uint8Array
      readonly format: 'rgb'
      readonly width: number
      readonly height: number
      readonly timestamp: number
    }
    const buildFrames = (count: number): FramePayload[] =>
      Array.from({ length: count }, () => ({
        data: rgbBytes,
        format: 'rgb' as const,
        width: targetSize,
        height: targetSize,
        timestamp: Date.now(),
      }))

    const batchedFrames = buildFrames(batchSize)
    const singleFrames = buildFrames(1)

    const callMsList: number[] = []
    const inferMsList: number[] = []
    let errors = 0
    let countedDetections = 0
    let totalCalls = 0
    let totalFrames = 0

    const dispatchOne = async (
      frames: readonly FramePayload[],
      isCounted: boolean,
      tag: string,
    ): Promise<void> => {
      const callStart = Date.now()
      try {
        const out = await this.api.pipelineExecutor.runPipelineBatch.mutate({
          nodeId,
          steps: [...steps],
          frames: [...frames],
          sessionId: sessionId ? `${sessionId}-${tag}` : tag,
        })
        const callMs = Date.now() - callStart
        if (isCounted) {
          callMsList.push(callMs)
          let maxInfer = 0
          for (const r of out.results) {
            const ms = r.debug?.totalInferenceMs ?? 0
            if (ms > maxInfer) maxInfer = ms
          }
          inferMsList.push(maxInfer)
        }
      } catch (err) {
        errors++
        this.ctx.logger.warn('runMultiCameraStressTest dispatch error', {
          meta: { tag, error: err instanceof Error ? err.message : String(err) },
        })
      }
    }

    const start = Date.now()
    const cameraTasks = Array.from({ length: simulatedCameras }, (_, camIdx) =>
      (async () => {
        for (let i = 0; i < batchesPerCamera; i++) {
          const dispatches: Promise<void>[] = [
            dispatchOne(batchedFrames, true, `c${camIdx}-r${i}-b`),
          ]
          for (let j = 0; j < extraSinglesPerRound; j++) {
            dispatches.push(dispatchOne(singleFrames, false, `c${camIdx}-r${i}-s${j}`))
          }
          await Promise.all(dispatches)
          countedDetections += batchSize
          totalCalls += 1 + extraSinglesPerRound
          totalFrames += batchSize + extraSinglesPerRound
        }
      })(),
    )

    await Promise.all(cameraTasks)
    const totalMs = Date.now() - start

    const sortedCall = [...callMsList].toSorted((a, b) => a - b)
    const pct = (xs: readonly number[], p: number): number => {
      if (xs.length === 0) return 0
      const idx = Math.min(xs.length - 1, Math.floor(xs.length * p))
      return xs[idx] ?? 0
    }
    const inferMean =
      inferMsList.length === 0 ? 0 : inferMsList.reduce((s, x) => s + x, 0) / inferMsList.length
    const detsPerSec = totalMs > 0 ? (countedDetections * 1000) / totalMs : 0

    this.ctx.logger.info('runMultiCameraStressTest done', {
      meta: {
        totalMs,
        countedDetections,
        detsPerSec: Number(detsPerSec.toFixed(2)),
        totalCalls,
        totalFrames,
        errors,
      },
    })

    return {
      totalMs,
      countedDetections,
      detsPerSec,
      totalCalls,
      totalFrames,
      callMsP50: pct(sortedCall, 0.5),
      callMsP95: pct(sortedCall, 0.95),
      callMsP99: pct(sortedCall, 0.99),
      inferMsMean: inferMean,
      errors,
    }
  }

  // ── Sustained throughput (root-detection e2e) ────────────────────────────

  /**
   * SUSTAINED THROUGHPUT test of ROOT detection.
   *
   * 1. Pin ONE frame in memory: resolve `frameId` (from `cacheBenchFrame`)
   *    or the one-shot `imageBase64`, decode + square-resize ONCE to raw
   *    RGB. Every node + every iteration consumes the IDENTICAL buffer — no
   *    per-iteration decode or upload.
   * 2. For each target node, run a `concurrency`-bounded loop of
   *    `runPipelineBatch` (batchSize frames/call) on the node's ROOT-ONLY
   *    steps until `durationSec` elapses (or the stop flag fires). Root-only
   *    steps stay on the batch single-root fast path == live `plane:'frame'`
   *    root detection.
   * 3. Aggregate sustained fps / det/s + latency percentiles per node on the
   *    identical pinned frame for apples-to-apples comparison.
   *
   * Non-destructive: targets the benchmark's own cached frame (never a live
   * camera session), is explicitly started, and is bounded by N + the stop
   * button (`abortSustainedThroughput`).
   */
  private async runSustainedThroughput(
    input: z.infer<BenchmarkActions['runSustainedThroughput']['input']>,
  ): Promise<z.infer<BenchmarkActions['runSustainedThroughput']['output']>> {
    const { frameId, imageBase64, durationSec, batchSize, concurrency, sessionId, nodes } = input
    const targetSize = input.targetSize ?? 640

    // Resolve the pinned frame source ONCE.
    const cached = frameId ? this.frameCache.get(frameId) : undefined
    if (frameId && !cached)
      throw new Error(
        `runSustainedThroughput: frameId ${frameId} not in cache (expired or never cached)`,
      )
    const jpegSource = cached
      ? cached.jpeg
      : imageBase64
        ? Buffer.from(imageBase64, 'base64')
        : null
    if (!jpegSource)
      throw new Error('runSustainedThroughput: no frame source (need frameId or imageBase64)')

    // Decode + square-resize ONCE. The raw RGB buffer is shared across every
    // node and every batch — the only decode of the whole run.
    const sharp = (await import('sharp')).default
    const rgb = await sharp(jpegSource)
      .resize({ width: targetSize, height: targetSize, fit: 'fill' })
      .raw()
      .toColorspace('srgb')
      .toBuffer()
    const rgbBytes = new Uint8Array(rgb.buffer, rgb.byteOffset, rgb.byteLength)

    // One reusable batch template — batchSize copies of the SAME pinned buffer.
    const batchFrames = Array.from({ length: batchSize }, () => ({
      data: rgbBytes,
      format: 'rgb' as const,
      width: targetSize,
      height: targetSize,
      timestamp: Date.now(),
    }))

    // Fresh nonzero uint32 `frameId` for this run. Passing it on every batch
    // pins the (identical) pinned frame to the pool's bench preprocess cache:
    // decode+preprocess runs ONCE (first batch, cache miss) and every later
    // inference is a pure-inference cache HIT. Without this the batch path
    // re-ran the full preprocess per inference — the measured fps was
    // decode+preprocess+infer, not inference (the ~78fps ceiling). Released
    // per node via `uncacheFrame` in the finally below.
    const benchFrameId = (Math.floor(Math.random() * 0xfffffffe) + 1) >>> 0

    const abortFlag = { aborted: false }
    this.sustainedAbortFlags.set(sessionId, abortFlag)

    this.ctx.logger.info('runSustainedThroughput start', {
      meta: {
        sessionId,
        durationSec,
        batchSize,
        concurrency,
        targetSize,
        nodeCount: nodes.length,
        nodes: nodes.map((n) => n.nodeId ?? 'hub'),
        deviceKeys: nodes.map((n) => n.deviceKey ?? '(default)'),
      },
    })

    try {
      // Fan out across nodes in parallel — each node is independent hardware
      // and its own inference pool is the bottleneck, not the hub's async
      // dispatch. Per-node numbers stay comparable on the identical frame.
      const perNode = await Promise.all(
        nodes.map(async (node) => {
          if (node.steps.length === 0)
            throw new Error(
              `runSustainedThroughput: node ${node.nodeId ?? 'hub'} has no root steps`,
            )

          const dispatchBatch = async (): Promise<BatchDispatchResult> => {
            const out = await this.api.pipelineExecutor.runPipelineBatch.mutate({
              nodeId: node.nodeId,
              steps: [...node.steps],
              frames: [...batchFrames],
              sessionId,
              frameId: benchFrameId,
              ...(node.engine ? { engine: node.engine } : {}),
              ...(node.deviceKey ? { deviceKey: node.deviceKey } : {}),
            })
            return {
              frameCount: out.results.length,
              detectionCounts: out.results.map((r) => r.detections.length),
            }
          }

          let raw: Awaited<ReturnType<typeof runSustainedLoop>>
          try {
            raw = await runSustainedLoop({
              dispatchBatch,
              concurrency,
              durationMs: durationSec * 1000,
              now: () => Date.now(),
              shouldStop: () => abortFlag.aborted,
            })
          } finally {
            // Release the pinned preprocess-cache entry on this node's pool so
            // a fresh benchmark frame never collides with a stale cached tensor
            // (and the pool doesn't accumulate one tensor per run).
            await this.api.pipelineExecutor.uncacheFrame
              .mutate({ nodeId: node.nodeId, frameId: benchFrameId })
              .catch(() => {})
          }

          const summary = {
            nodeId: node.nodeId ?? null,
            ...(node.label ? { label: node.label } : {}),
            totalFrames: raw.totalFrames,
            totalBatches: raw.totalBatches,
            wallMs: raw.wallMs,
            fps: computeFps(raw.totalFrames, raw.wallMs),
            detsPerSec: computeDetsPerSec(raw.totalDetections, raw.wallMs),
            avgDetections: computeAvgDetections(raw.totalDetections, raw.detectionSamples),
            latency: computeLatencyStats(raw.callMsList),
            errors: raw.errors,
            aborted: raw.aborted,
          }
          this.ctx.logger.info('runSustainedThroughput node done', {
            meta: {
              nodeId: summary.nodeId,
              fps: Number(summary.fps.toFixed(2)),
              detsPerSec: Number(summary.detsPerSec.toFixed(2)),
              p95Ms: Number(summary.latency.p95.toFixed(2)),
              totalFrames: summary.totalFrames,
              errors: summary.errors,
              aborted: summary.aborted,
            },
          })
          return summary
        }),
      )

      return { perNode, durationSec, batchSize, concurrency }
    } finally {
      this.sustainedAbortFlags.delete(sessionId)
    }
  }

  private async abortSustainedThroughput(
    input: z.infer<BenchmarkActions['abortSustainedThroughput']['input']>,
  ): Promise<z.infer<BenchmarkActions['abortSustainedThroughput']['output']>> {
    const flag = this.sustainedAbortFlags.get(input.sessionId)
    if (!flag) return { aborted: false }
    flag.aborted = true
    return { aborted: true }
  }

  // ── Decoder perf — fixture discovery + run orchestration ────────────────

  private async listDecoderFixtures(): Promise<readonly DecoderFixture[]> {
    const dir = resolveFixturesDir()
    if (!fs.existsSync(dir)) {
      this.ctx.logger.warn('decoder fixtures directory missing', { meta: { dir } })
      return []
    }
    const out: DecoderFixture[] = []
    for (const entry of fs.readdirSync(dir, { withFileTypes: true })) {
      if (!entry.isFile()) continue
      const meta = FIXTURE_META[entry.name]
      if (!meta) continue
      const full = path.join(dir, entry.name)
      const stat = fs.statSync(full)
      out.push({
        filename: entry.name,
        codec: meta.codec,
        resolutionLabel: meta.resolutionLabel,
        sizeBytes: stat.size,
        description: meta.description,
      })
    }
    out.sort((a, b) => a.sizeBytes - b.sizeBytes)
    return out
  }

  private async runDecoderPerformanceTest(
    input: z.infer<BenchmarkActions['runDecoderPerformanceTest']['input']>,
  ): Promise<z.infer<BenchmarkActions['runDecoderPerformanceTest']['output']>> {
    const abortFlag = { aborted: false }
    this.decoderPerfAbortFlags.set(input.sessionId, abortFlag)
    try {
      return await runDecoderPerfTest(input, {
        api: this.api,
        eventBus: this.ctx.eventBus,
        logger: this.ctx.logger,
        fixturesDir: resolveFixturesDir(),
        abortFlag,
      })
    } finally {
      this.decoderPerfAbortFlags.delete(input.sessionId)
    }
  }

  private async abortDecoderPerformanceTest(
    input: z.infer<BenchmarkActions['abortDecoderPerformanceTest']['input']>,
  ): Promise<z.infer<BenchmarkActions['abortDecoderPerformanceTest']['output']>> {
    const flag = this.decoderPerfAbortFlags.get(input.sessionId)
    if (!flag) return { aborted: false }
    flag.aborted = true
    return { aborted: true }
  }

  // ── History persistence (per-addon store on the hub) ──────────────────

  /**
   * Durable handle over the persisted history blob. LEGACY SHAPE, PRESERVED
   * VERBATIM: the value under `benchmarkHistory` is a JSON **string** (not
   * the array itself), so the handle is typed over the string — the
   * encode/decode + entry-level recovery below stays the codec, the handle
   * owns store access (retry budget + validated fallback). Absent key or a
   * non-string value falls back to the empty-history encoding, exactly the
   * `[]` the raw read produced.
   */
  private historyState(): DurableState<string> {
    return this.state<string>(KEY_BENCHMARK_HISTORY, z.string(), EMPTY_HISTORY_JSON)
  }

  private async loadHistory(): Promise<HistoryEntry[]> {
    const raw = await this.historyState().get()
    try {
      const parsed = parseJsonUnknown(raw)
      const result = HistoryArraySchema.safeParse(parsed)
      if (result.success) return [...result.data]

      // Entry-level recovery. The array-level parse rejects everything
      // when one entry is malformed — walk entries one at a time so one
      // bad record doesn't hide the rest. For each entry we also apply
      // the legacy default of `kind: 'pipeline'` (the only kind that
      // existed before the discriminated-union rewrite) before parsing.
      const arr: readonly unknown[] = Array.isArray(parsed) ? parsed : []
      const accepted: HistoryEntry[] = []
      let rejected = 0
      for (const entry of arr) {
        const normalized =
          entry !== null && typeof entry === 'object' && !('kind' in entry)
            ? { ...(entry as Record<string, unknown>), kind: 'pipeline' as const }
            : entry
        const parsedEntry = HistoryEntrySchema.safeParse(normalized)
        if (parsedEntry.success) {
          accepted.push(parsedEntry.data)
        } else {
          rejected += 1
        }
      }
      if (rejected > 0) {
        this.ctx.logger.warn('benchmark history: dropped unparseable entries', {
          meta: { rejected, accepted: accepted.length, error: result.error.message },
        })
      }
      return accepted
    } catch (err: unknown) {
      this.ctx.logger.warn('benchmark history parse failed', { meta: { error: errMsg(err) } })
      return []
    }
  }

  private async writeHistory(history: readonly HistoryEntry[]): Promise<void> {
    await this.historyState().set(JSON.stringify(history))
  }

  private async listBenchmarkHistory(): Promise<readonly HistoryEntry[]> {
    return this.loadHistory()
  }

  private async saveBenchmarkResult(
    entry: z.infer<BenchmarkActions['saveBenchmarkResult']['input']>,
  ): Promise<PipelineHistoryEntry> {
    const history = await this.loadHistory()
    const saved: PipelineHistoryEntry = {
      ...entry,
      kind: 'pipeline',
      id: `bench-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      date: new Date().toISOString(),
    }
    const next = [saved, ...history]
    if (next.length > MAX_HISTORY_ENTRIES) next.length = MAX_HISTORY_ENTRIES
    await this.writeHistory(next)
    return saved
  }

  private async saveDecoderPerformanceResult(
    entry: z.infer<BenchmarkActions['saveDecoderPerformanceResult']['input']>,
  ): Promise<DecoderPerfHistoryEntry> {
    const history = await this.loadHistory()
    const saved: DecoderPerfHistoryEntry = {
      kind: 'decoder-perf',
      id: `decoder-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      date: new Date().toISOString(),
      ...entry,
    }
    const next: HistoryEntry[] = [saved, ...history]
    if (next.length > MAX_HISTORY_ENTRIES) next.length = MAX_HISTORY_ENTRIES
    await this.writeHistory(next)
    return saved
  }

  private async deleteBenchmarkResult(input: { id: string }): Promise<{ success: true }> {
    const history = (await this.loadHistory()).filter((e) => e.id !== input.id)
    await this.writeHistory(history)
    return { success: true }
  }

  private async clearBenchmarkHistory(): Promise<{ success: true }> {
    await this.writeHistory([])
    return { success: true }
  }
}
